import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import type {
  BrewcontrolNodeId,
  BrewcontrolWorkspaceId,
} from "@brewbase/db/types";
import { toast } from "sonner";
import { trpc } from "@/hooks/trpc";
import { useCan } from "@/hooks/use-operator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DocumentEditorSession,
  ScopeStatus,
  type ConfigScope,
} from "@/features/brewcontrol/config-editor";
import { ConfigDocumentDialog } from "@/features/brewcontrol/config-document-dialog";
import { ConfigDocumentRow } from "@/features/brewcontrol/config-document-row";
import { ConfigHistory } from "@/features/brewcontrol/config-history";
import { DeviceConfigEditor } from "@/features/brewcontrol/device-config-editor";
import { WorkspaceConfigBlock } from "@/features/brewcontrol/workspace-config-block";

export const Route = createFileRoute("/brewcontrol/$nodeId/config")({
  component: NodeConfig,
});

/**
 * The node's shape, authored here, laid out in commissioning order: devices,
 * calibration, then each workspace's vessel, wiring and tuning. Every document
 * is edited in a workspace dialog with its own Apply, because every save mints
 * a version and pushes it. The changelog of what the node took sits alongside.
 */
function NodeConfig() {
  const { nodeId } = Route.useParams();
  const canEdit = useCan("brewcontrol-nodes:manage");
  const utils = trpc.useUtils();
  const { data: authoring } = trpc.brewcontrolConfig.nodeAuthoring.useQuery(
    { nodeId },
    { refetchInterval: 5_000 },
  );
  const { data: status } = trpc.brewcontrolConfig.status.useQuery(
    { nodeId },
    { refetchInterval: 3_000 },
  );

  const refresh = () => utils.brewcontrolConfig.invalidate();
  const onError = (e: { message: string }) => toast.error(e.message);
  const saveDevices = trpc.brewcontrolConfig.saveDevices.useMutation({
    onSuccess: refresh,
    onError,
  });
  const saveTuning = trpc.brewcontrolConfig.saveTuning.useMutation({
    onSuccess: refresh,
    onError,
  });
  const saveCalibration = trpc.brewcontrolConfig.saveCalibration.useMutation({
    onSuccess: refresh,
    onError,
  });
  const createWorkspace = trpc.brewcontrolConfig.createWorkspace.useMutation({
    onSuccess: refresh,
    onError,
  });
  const renameWorkspace = trpc.brewcontrolConfig.renameWorkspace.useMutation({
    onSuccess: refresh,
    onError,
  });
  const deleteWorkspace = trpc.brewcontrolConfig.deleteWorkspace.useMutation({
    onSuccess: refresh,
    onError,
  });
  const [newName, setNewName] = useState("");

  if (!status) {
    return <div className="px-6 py-6 text-sm text-olive-11">Loading…</div>;
  }

  const scope = (
    kind: `${ConfigScope["kind"]}`,
    workspaceId: BrewcontrolWorkspaceId | null,
  ) =>
    status.scopes.find(
      (s) => (s.kind as string) === kind && s.workspaceId === workspaceId,
    );
  const devices = scope("devices", null);
  const calibration = scope("calibration", null);
  const calibrationCount = parseEntries(calibration?.desired?.content);
  const refused = status.scopes.filter((s) => s.rejection).length;

  return (
    <div className="grid min-h-full grid-cols-[minmax(0,1fr)_20rem]">
      <div className="min-w-0 space-y-6 px-6 py-6">
        <header className="space-y-1">
          <h1 className="text-sm font-medium">Config</h1>
          <p className="text-xs text-olive-11">
            {status.connected
              ? status.reported
                ? "Connected and reporting."
                : "Connected, has not reported what it runs yet."
              : "Offline. Saved versions are pushed when it reconnects."}
          </p>
          {refused > 0 && (
            <p className="inline-block bg-red-50 px-2 py-1 text-xs text-red-800">
              The node refused {refused} document{refused === 1 ? "" : "s"}. See
              the status next to each, or the changelog.
            </p>
          )}
        </header>

        <div>
          {devices && (
            <DeviceConfigEditor
              key={nodeId}
              scope={devices}
              reported={status.reported}
              canEdit={canEdit}
              pending={saveDevices.isPending}
              onApply={(content, expectedVersionId) =>
                saveDevices.mutateAsync({ nodeId, content, expectedVersionId })
              }
              catalog={authoring?.deviceCatalog}
              workspaces={status.workspaces.map((w) => ({
                id: w.bc_workspace_id,
                label: w.display_name ?? `Workspace ${w.bc_workspace_id}`,
              }))}
              signals={authoring?.signals}
              aliases={authoring?.aliases}
            />
          )}
          {calibration && (
            <ConfigDocumentRow
              label="Calibration"
              summary={`${calibrationCount ?? "—"} entries`}
              status={
                <ScopeStatus scope={calibration} reported={status.reported} />
              }
              action={
                <ConfigDocumentDialog label={canEdit ? "Edit" : "View"}>
                  {({ requestClose, close }) => (
                    <DocumentEditorSession
                      title="Calibration"
                      description="Probe calibrations the node applies. Routines write this; edit by hand only to remove or correct an entry."
                      scope={calibration}
                      reported={status.reported}
                      canEdit={canEdit}
                      pending={saveCalibration.isPending}
                      onApply={(content, expectedVersionId) =>
                        saveCalibration.mutateAsync({
                          nodeId,
                          content,
                          expectedVersionId,
                        })
                      }
                      requestClose={requestClose}
                      close={close}
                    />
                  )}
                </ConfigDocumentDialog>
              }
            />
          )}
        </div>

        <section className="space-y-4">
          <h2 className="text-sm font-medium">Workspaces</h2>
          {status.workspaces.length === 0 && (
            <div className="text-sm text-olive-11">
              No workspaces. Add one to link a vessel.
            </div>
          )}
          {status.workspaces.map((w) => (
            <WorkspaceConfigBlock
              key={w.id}
              workspace={w}
              authoring={authoring}
              devicesContent={authoring?.appliedDevicesContent ?? "[]"}
              wiring={scope("wiring", w.id)}
              tuning={scope("tuning", w.id)}
              reported={status.reported}
              canEdit={canEdit}
              saving={saveTuning.isPending}
              onTuning={(content, expectedVersionId) =>
                saveTuning.mutateAsync({
                  workspaceId: w.id,
                  content,
                  expectedVersionId,
                })
              }
              onRename={(displayName) =>
                renameWorkspace.mutate({ id: w.id, displayName })
              }
              onDelete={() => deleteWorkspace.mutate({ id: w.id })}
              onVesselChanged={refresh}
            />
          ))}
          {canEdit && (
            <form
              className="flex items-center gap-2 border-t border-olive-5 pt-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (!newName.trim()) return;
                createWorkspace.mutate({ nodeId, displayName: newName.trim() });
                setNewName("");
              }}
            >
              <Input
                className="w-48"
                placeholder="New workspace name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
              <Button
                size="sm"
                type="submit"
                disabled={createWorkspace.isPending}
              >
                Add
              </Button>
            </form>
          )}
        </section>
      </div>

      <aside className="sticky top-0 h-dvh space-y-2 overflow-y-auto border-l border-olive-5 px-4 py-6">
        <h2 className="text-sm font-medium">Changelog</h2>
        <ConfigHistory nodeId={nodeId as BrewcontrolNodeId} canEdit={canEdit} />
      </aside>
    </div>
  );
}

function parseEntries(content: string | undefined): number | null {
  if (!content) return 0;
  try {
    const parsed: unknown = JSON.parse(content);
    return Array.isArray(parsed) ? parsed.length : null;
  } catch {
    return null;
  }
}
